import { Model } from 'app/model/backendmodel';
import { DatasetAdapter } from './dataset.adapter';
import {
    IModel,
    TabularTypeDataset,
    ReferenceAdapter,
    IAttribute,
    isLogicAppExpression,
    SharedModel
} from 'shell-models-lib';
import { ValueAdapter } from 'app/model/adapters/value.adapter';
import _ from 'lodash';
import { Metamodel } from 'app/model/base/model';
import { type DatasetRegistryService } from 'app/registries/dataset/dataset-registry.service';
import { LinkedServiceRegistryService } from 'app/registries/linked-service/linked-service-registry.service';
import { getServiceInstance } from 'app/multi-contexts/modules/root/root.application';
import { IDatabaseConnectionContext } from 'app/interfaces/copilot.pipeline.action.types';

export const tabularPropertiesCommonAdapts = {
    description: '*',
    folder: '*',
    annotations: '*',
    structure: '*',
    schema: '*',
    linkedServiceName: '*',
    parameters: '*',
    type: '*'
};

export const tabularTypeDatasetAdapts = {
    name: '*',
    properties: {
        ...tabularPropertiesCommonAdapts,
        typeProperties: {
            tableName: '*',
            schema: '*',
            table: '*',
        }
    }
};

export const tabularTypeDatasetWithDatabaseAdapts = {
    name: '*',
    properties: {
        ...tabularPropertiesCommonAdapts,
        typeProperties: {
            tableName: '*',
            schema: '*',
            table: '*',
            database: '*'
        }
    }
};

export abstract class TabularTypeDatasetAdapter<
    T extends TabularTypeDataset,
    U extends Model.DatasetResource,
    D extends IDatabaseConnectionContext = IDatabaseConnectionContext
> extends DatasetAdapter<T, U, D> {
    public override async toFabricCopilotContext(dataset: T): Promise<D> {
        if (dataset) {
            const dsContext = await super.toFabricCopilotContext(dataset);
            const dsSvc = await getServiceInstance<DatasetRegistryService>(
                this.context.contextData,
                'DatasetRegistryService'
            );
            if (dsSvc) {
                const dsModel = Metamodel.modelOf(dataset);
                const dsMetadata = dsSvc.getDatasetConfiguration(dsModel.name);
                let schema, table, database;
                if (!dsMetadata.splitTablePath) {
                    table = ValueAdapter.toResource(dataset, TabularTypeDataset.TABLE_NAME, true);
                } else {
                    schema = ValueAdapter.toResource(dataset, TabularTypeDataset.SCHEMA, true);
                    table = ValueAdapter.toResource(dataset, TabularTypeDataset.TABLE, true);
                }
                if (dsModel.hasAttribute('database')) {
                    database = ValueAdapter.toResource(dataset, dsModel.attribute('database'), true);
                }
                const result = {
                    ...dsContext,
                    Schema: schema,
                    Table: table,
                    Database: database
                };
                return Object.keys(result).length > 0 ? (result as D) : undefined;
            }
        }
        return undefined;
    }

    public override async fromFabricCopilotContext(context: D, model?: T): Promise<T> {
        if (context) {
            model = await super.fromFabricCopilotContext(context, model);
            const datasetRegistryService = await getServiceInstance<DatasetRegistryService>(
                this.context.contextData,
                'DatasetRegistryService'
            );
            const dsMetadata = datasetRegistryService.getDatasetConfiguration(Metamodel.modelOf(model).name);
            if (!dsMetadata.splitTablePath) {
                ValueAdapter.toModel(model, TabularTypeDataset.TABLE_NAME, context.Table ?? undefined);
            } else {
                ValueAdapter.toModel(model, TabularTypeDataset.SCHEMA, context.Schema ?? undefined);
                ValueAdapter.toModel(model, TabularTypeDataset.TABLE, context.Table ?? undefined);
            }
            if (Metamodel.modelOf(model).hasAttribute('database')) {
                ValueAdapter.toModel(model, Metamodel.modelOf(model).attribute('database'), context.Database ?? undefined);
            }
        }
        return model;
    }

    protected async _toResourceInternal(
        dataset: T,
        datasetType: string,
        tableNameKeyword?: string,
        schemaKeyword?: string,
        tableKeyword?: string,
        notHandleSchema?: boolean,
        databaseAttribute?: IAttribute
    ): Promise<Model.DatasetResource> {
        const modelDatasetResource = (await super._toResource(dataset)) as Model.DatasetResource;
        const modelDataset = this.properties(modelDatasetResource) as Model.Dataset;
        modelDataset.type = datasetType;
        if (!notHandleSchema) {
            if (dataset.isV2) {
                modelDataset.schema = this.columnValueToResource(dataset, dataset.columns);
            } else {
                modelDataset.structure = this.columnValueToResource(dataset, dataset.columns);
            }
        }
        const tableName = ValueAdapter.toResource(dataset, TabularTypeDataset.TABLE_NAME);
        if (tableName) {
            tableNameKeyword = tableNameKeyword || 'tableName';
            modelDataset.typeProperties = {
                [tableNameKeyword]: tableName
            };
        } else {
            schemaKeyword = schemaKeyword || 'schema';
            tableKeyword = tableKeyword || 'table';
            modelDataset.typeProperties = {
                [schemaKeyword]: ValueAdapter.toResource(dataset, TabularTypeDataset.SCHEMA),
                [tableKeyword]: ValueAdapter.toResource(dataset, TabularTypeDataset.TABLE)
            };
        }
        // currently, this is used for SQL family, and can be extended to other datasets that contains database
        if (databaseAttribute) {
            modelDataset.typeProperties.database = ValueAdapter.toResource(dataset, databaseAttribute) || undefined;
        }
        if (dataset.raw) {
            const parsed = JSON.parse(dataset.raw);
            _.merge(modelDatasetResource, parsed);
        }
        return modelDatasetResource;
    }

    protected async _toModelInternal(
        modelDatasetResource: U,
        dataset: T,
        datasetModel: IModel,
        tableNameKeyword?: string,
        schemaKeyword?: string,
        tableKeyword?: string,
        notHandleSchema?: boolean,
        databaseAttribute?: IAttribute
    ): Promise<T> {
        if (!dataset) {
            dataset = datasetModel.create<T>(modelDatasetResource.name);
        }
        await super._toModel(modelDatasetResource, dataset);
        const model = modelDatasetResource.properties as Model.Dataset;
        if (model) {
            if (model.typeProperties) {
                tableNameKeyword = tableNameKeyword || 'tableName';
                schemaKeyword = schemaKeyword || 'schema';
                tableKeyword = tableKeyword || 'table';

                ValueAdapter.toModel(dataset, TabularTypeDataset.TABLE_NAME, model.typeProperties[tableNameKeyword]);
                ValueAdapter.toModel(dataset, TabularTypeDataset.SCHEMA, model.typeProperties[schemaKeyword]);
                ValueAdapter.toModel(dataset, TabularTypeDataset.TABLE, model.typeProperties[tableKeyword]);

                // currently, this is used for SQL family, and can be extended to other datasets that contains database
                if (databaseAttribute) {
                    ValueAdapter.toModel(dataset, databaseAttribute, model.typeProperties.database);
                }
            }
            // we assume all datasets with schema will be V2 dataset
            dataset.isV2 = model.schema != null;
            if (!notHandleSchema) {
                const cols = dataset.isV2 ? model.schema : model.structure;
                if (!dataset.isV2) {
                    // v1
                    dataset.columns = this.columnResourceToValue(dataset, cols);
                } else if (Array.isArray(cols)) {
                    // v2
                    dataset.columns = this.columnResourceToValue(dataset, cols);
                    dataset.columns.forEach((c) => {
                        c.physicalType = c.columnType;
                    });
                }
            }
        }
        if (
            this.context.adapterConfig.tolerateInvalidReference &&
            modelDatasetResource?.properties?.linkedServiceName &&
            !isLogicAppExpression(
                (modelDatasetResource.properties.linkedServiceName as SharedModel.LinkedServiceReference).referenceName
            )
        ) {
            const linkedServiceModel = datasetModel.attribute('linkedService').constraint.model;
            dataset.linkedService = ReferenceAdapter.toProxyModel(
                linkedServiceModel,
                modelDatasetResource?.properties?.linkedServiceName,
                this.context
            );
        }
        dataset.raw = JSON.stringify(await ValueAdapter.remainder(this.context, modelDatasetResource, datasetModel));
        return dataset;
    }

    protected async _toResource(dataset: T): Promise<U> {
        const dsConfig = (
            await getServiceInstance<DatasetRegistryService>(this.context.contextData, 'DatasetRegistryService')
        ).getDatasetConfiguration(Metamodel.modelOf(dataset).name);
        const lsConfig = (
            await getServiceInstance<LinkedServiceRegistryService>(
                this.context.contextData,
                'LinkedServiceRegistryService'
            )
        ).getLinkedServiceConfiguration(dsConfig.supportLinkedServiceTypes[0]);
        return (await this._toResourceInternal(
            dataset,
            Metamodel.modelOf(dataset).name,
            lsConfig.tableNameAlias,
            dsConfig.schemaAlias,
            dsConfig.tableAlias
        )) as U;
    }

    protected async _toModel(modelDatasetResource: U, dataset?: T): Promise<T> {
        const dsModel = Metamodel.modelByName(modelDatasetResource.properties.type);
        const dsConfig = (
            await getServiceInstance<DatasetRegistryService>(this.context.contextData, 'DatasetRegistryService')
        ).getDatasetConfiguration(dsModel.name);
        const lsConfig = (
            await getServiceInstance<LinkedServiceRegistryService>(
                this.context.contextData,
                'LinkedServiceRegistryService'
            )
        ).getLinkedServiceConfiguration(dsConfig.supportLinkedServiceTypes[0]);

        return await this._toModelInternal(
            modelDatasetResource,
            dataset,
            dsModel,
            lsConfig.tableNameAlias,
            dsConfig.schemaAlias,
            dsConfig.tableAlias
        );
    }
}
